<template>
  <div class="upload-task-item" @dblclick="previewItem">
    <div :class="[uploadTaskItemIcon, { 'loading-animation': props.fileInfo.status === UploadItemStatus.UPLOADING }]">
      <div class="loader-1">
        <span />
      </div>
    </div>
    <div class="upload-task-item-body">
      <div class="upload-task-item-filename">{{ uploadTaskItemName }}</div>
      <div class="upload-task-item-size" v-if="isIdleOrUploading">
        {{ formatBytes(props.fileInfo.size) }}
      </div>
      <div :class="statusClass" v-else>
        {{ statusMsg(props.fileInfo.status) }}
      </div>
    </div>
    <EyeButton @click="previewItem" :toggleable="false" class="upload-task-item-preview" />
    <button @click="removeItem" @dblclick.stop class="upload-task-item-remove" v-if="isNotUploadedNorUploading" ></button>
  </div>
</template>

<script setup>
  import EyeButton from './EyeButton.vue'
  import { UploadItemStatus } from '@/utils/constant'
  import { ref, computed, nextTick } from 'vue';
  import { $T, getTextWidth } from '@/utils/texts'
  import { extension, getIconClass } from '@/utils/get-icon'
  import { formatBytes } from '@/utils/format-bytes'
  import { formatString } from '@/utils/texts/index';

  const props = defineProps({
    fileInfo: {
      type: Object,
      required: true
    }
  })

  const fileNameContainerWidth = ref(314) // default value
  const emits = defineEmits(['remove', 'preview'])
  const removeItem = () => {
    emits('remove')
  }

  const previewItem = () => {
    emits('preview')
  }

  const statusClass = computed(() => {
    return {
      'upload-task-item-size': props.fileInfo.status === UploadItemStatus.WAITING,
      'upload-task-item-failed': props.fileInfo.status === UploadItemStatus.FAILED || props.fileInfo.status === UploadItemStatus.PARTIAL_FAILED || props.fileInfo.status === UploadItemStatus.SIZE_EXCEED,
      'upload-task-item-success': props.fileInfo.status === UploadItemStatus.SUCCESS
    }
  })

  const uploadTaskItemIcon = computed(() => {
    const cls = 'upload-task-item-icon'

    if (props.fileInfo.type === 'folder') {
      return cls + ' folder'
    }

    return cls + ' ' + getIconClass(extension(props.fileInfo.file.name))
  })

  const isIdleOrUploading = computed(() => {
    return props.fileInfo.status === UploadItemStatus.IDLE || props.fileInfo.status === UploadItemStatus.UPLOADING
  })

  const isNotUploadedNorUploading = computed(() => {
    return props.fileInfo.status !== UploadItemStatus.SUCCESS && props.fileInfo.status !== UploadItemStatus.UPLOADING
  })

  const statusMsg = (status) => {
    if (status === UploadItemStatus.WAITING) {
      return $T('file_request', 'waiting')
    }
    if (status === UploadItemStatus.SIZE_EXCEED) {
      if (props.fileInfo.type === 'folder') {
        return $T('file_request', 'include_large_files')
      }

      return formatString($T('file_request', 'size_larger_than'), formatBytes(props.fileInfo.size))
    }
    if (status === UploadItemStatus.FAILED) {
      return props.fileInfo.errMsg || 'Failed'
    }
    if (status === UploadItemStatus.PARTIAL_FAILED) {
      return 'Partial Failed'
    }
    if (status === UploadItemStatus.SUCCESS) {
      return $T('file_request', 'success')
    }

    return ''
  }

  const uploadTaskItemName = computed(() => {
    const getUniversalFontStyle = () => {
      const titleClass = '.request-title'
      const titleDom = document.querySelector(titleClass)
      const titleStyle = window.getComputedStyle(titleDom)
      return `${titleStyle['font-size']} ${titleStyle['font-family']}`
    }

    const textFont = getUniversalFontStyle()
    const width = getTextWidth(props.fileInfo.file.name, textFont)

    if (width <= fileNameContainerWidth.value) {
      return props.fileInfo.file.name
    }

    const fileName = props.fileInfo.file.name
    const fileNameLength = fileName.length
    const ellipsisWidth = getTextWidth(' ... ', textFont)
    const frontBackWidth = Math.floor((fileNameContainerWidth.value - ellipsisWidth) / 2)

    let frontChars = 5
    let frontWidth = 0

    while (frontWidth < frontBackWidth && frontChars < fileNameLength) {
        frontWidth = getTextWidth(fileName.slice(0, frontChars), textFont)
        frontChars++
    }
    frontChars--

    let backChars = 5
    let backWidth = 0

    while (backWidth < frontBackWidth && backChars < fileNameLength) {
        backWidth = getTextWidth(fileName.slice(fileNameLength - backChars), textFont)
        backChars++
    }
    backChars--

    return `${fileName.slice(0, frontChars)}  ... ${fileName.slice(fileNameLength - backChars)}`
  })

  nextTick(() => {
    const targetClass = '.upload-task-item-body'
    const container = document.querySelector(targetClass)

    if (container && container.offsetWidth < fileNameContainerWidth.value) {
      fileNameContainerWidth.value = container.offsetWidth
    }
  })

</script>

<style lang="scss" scoped>
@import '@/mixins/style.scss';

.upload-task-item {
  display: flex;
  width: 478px;
  height: 56px;
  padding: 8px 24px;
  align-items: center;
  gap: 12px;
  box-sizing: border-box;
  &:hover {
    background: var(--v-item-bg-tier1);
  }
}

.upload-task-item-icon {
  display: flex;
  width: 32px;
  height: 32px;
  padding: 0px 0.2px 0.2px 0px;
  justify-content: center;
  align-items: center;
  flex-shrink: 0;
  box-sizing: border-box
}

.loading-animation {
  @include loading-animation-icon(32, 24);
}

.folder {
  background-image: url('@/assets/filetype/folder.png');
  background-size: 100%;
}

.upload-task-item-body {
  width: 314px;
  display: flex;
  flex-direction: column;
  text-align: left;
}
.upload-task-item-filename {
  color: var(--v-color-font-tier1);
  font-size: 14px;
  line-height: 20px;
  white-space: nowrap;
  overflow: hidden;
}
.upload-task-item-size {
  color: var(--v-color-font-tier2);
  font-size: 12px;
  font-style: normal;
  font-weight: 400;
  line-height: 20px;
}
.upload-task-item-success {
  color: var(--v-color-green);
  font-size: 12px;
  font-style: normal;
  font-weight: 400;
  line-height: 20px;
}

.upload-task-item-failed {
  color: var(--v-color-red);
  font-size: 12px;
  font-style: normal;
  font-weight: 400;
  line-height: 20px;
}

.upload-task-item-preview {
  width: 24px;
  height: 24px;
  border: none;
}
.upload-task-item-remove {
  display: none;
  width: 24px;
  height: 24px;
  border: none;
  cursor: pointer;
  background-image: url('@/assets/icon_close_small.svg');
  background-color: transparent;

  &:hover {
    background-position: 0 -24px;
  }
  &:active {
    background-position: 0 -48px;
  }
}

.upload-task-item-preview {
  display: none;
}

.upload-task-item:hover {
  .upload-task-item-remove, .upload-task-item-preview {
    display: block;
  }
}

@media (max-width: 624px) {
  .upload-task-item-body {
    min-width: 0;
    display: flex;
    flex-direction: column;
    text-align: left;
    .upload-task-item-filename {
      color: var(--v-color-font-tier1);
      font-size: 13px;
      line-height: 13px;
      white-space: nowrap;
      overflow: hidden;
      padding-bottom: 3px;
    }
  }

  .upload-task-item {
    width: 100%;
    .upload-task-item-preview, .upload-task-item-preview:hover, .upload-task-item-preview:active {
      display: none !important;
    }
  }
}

</style>

